import React, { useEffect, useState } from "react";
import {
  Globe,
  Users,
  Building2,
  Tag,
  Plus,
  Edit2,
  Trash2,
  Settings,
  Mail,
  MailOpen,
} from "lucide-react";
import Swal2 from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import AddStatus from "./addStatus";
import AddUser from "./addUser";
import "./app.css";
import { secretKey } from "./data";
import Loading from "./loading.js";
import AddMail from "./addMail";
import AddMailTemplate from "./addMailTemplate";

const MySwal = withReactContent(Swal2);
const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };

const TABS = [
  { id: 0, label: "Źródła", icon: Globe },
  { id: 2, label: "Działy", icon: Building2 },
  { id: 1, label: "Użytkownicy", icon: Users },
  { id: 3, label: "Statusy", icon: Tag },
  { id: 4, label: "Maile", icon: Mail },
  { id: 5, label: "Szablony maili", icon: MailOpen },
];

const AdminPanel = (props) => {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [mails, setMails] = useState([]);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);

  /* modals */
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showAddStatus, setShowAddStatus] = useState(false);
  const [showEditStatus, setShowEditStatus] = useState(false);
  const [editStatus, setEditStatus] = useState(null);
  const [showEditMail, setShowEditMail] = useState(false);
  const [editMail, setEditMail] = useState(null);
  const [showAddMail, setShowAddMail] = useState(false);
  const [showAddMailTemplate, setShowAddMailTemplate] = useState(false);
  const [editMailTemplate, setEditMailTemplate] = useState(null);
  const [showEditMailTemplate, setShowEditMailTemplate] = useState(false);

  /* ── fetch (parallel) ── */
  const getData = async () => {
    setLoading(true);
    try {
      const [src, usr, comp, stat, mails, mailTemplates] = await Promise.all([
        fetch(`${API}/getLeadSources`, {
          method: "POST",
          headers: hdrs,
          body: "{}",
        }).then((r) => r.json()),
        fetch(`${API}/getUsers`, {
          method: "POST",
          headers: hdrs,
          body: "{}",
        }).then((r) => r.json()),
        fetch(`${API}/getCompanies`, {
          method: "POST",
          headers: hdrs,
          body: "{}",
        }).then((r) => r.json()),
        fetch(`${API}/getStatuses`, {
          method: "POST",
          headers: hdrs,
          body: "{}",
        }).then((r) => r.json()),
        fetch(`${API}/getMails`, {
          method: "POST",
          headers: hdrs,
          body: "{}",
        }).then((r) => r.json()),
        fetch(`${API}/getMailTemplates`, {
          method: "POST",
          headers: hdrs,
          body: "{}",
        }).then((r) => r.json()),
      ]);
      setSources(src || []);
      setUsers(usr || []);
      setCompanies(comp || []);
      setStatuses(stat || []);
      setMails(mails || []);
      setMailTemplates(mailTemplates || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  /* ── CRUD helpers ── */
  const postAndRefresh = async (endpoint, body, successTitle, successText) => {
    await fetch(`${API}/${endpoint}`, {
      method: "POST",
      headers: hdrs,
      body: JSON.stringify(body),
    });
    MySwal.fire({ icon: "success", title: successTitle, text: successText });
    getData();
  };

  const promptAdd = (title, placeholder, onSubmit) => {
    MySwal.fire({
      title,
      input: "text",
      inputLabel: "wartość",
      inputPlaceholder: placeholder,
      cancelButtonText: "Anuluj",
      showCancelButton: true,
      inputValidator: (v) => {
        if (!v) return "Wpisz wartość";
        onSubmit(v);
      },
    });
  };

  /* sources */
  const addSource = () =>
    promptAdd("Dodaj źródło", "Nazwa źródła", (v) =>
      postAndRefresh("addSource", { name: v }, "Źródło", "dodane")
    );
  const deleteSource = (id) =>
    postAndRefresh("deleteSource", { id }, "Źródło", "usunięte");

  /* companies */
  const addCompany = () =>
    promptAdd("Dodaj dział", "Nazwa działu", (v) =>
      postAndRefresh("addCompany", { name: v }, "Dział", "dodany")
    );
  const deleteCompany = (id) =>
    postAndRefresh("deleteCompany", { id }, "Dział", "usunięty");

  /* ── close modals ── */
  const closeModal = (setter) => {
    setter(false);
    getData();
  };

  if (loading) return <Loading />;

  /* ── current tab add action ── */
  const addActions = {
    0: { label: "Dodaj źródło", onClick: addSource },
    2: { label: "Dodaj dział", onClick: addCompany },
    1: { label: "Dodaj użytkownika", onClick: () => setShowAddUser(true) },
    3: { label: "Dodaj status", onClick: () => setShowAddStatus(true) },
    4: { label: "Dodaj mail", onClick: () => setShowAddMail(true) },
    5: {
      label: "Dodaj szablon maila",
      onClick: () => setShowAddMailTemplate(true),
    },
  };
  const currentAdd = addActions[tab];

  return (
    <div className="adm-wrapper">
      {/* ── Modals ── */}
      {showAddUser && (
        <AddUser
          user={props.user}
          mode="add"
          close={() => closeModal(setShowAddUser)}
          sources={sources}
          companies={companies}
        />
      )}
      {showEditUser && editUser && (
        <AddUser
          user={props.user}
          userEdit={editUser}
          mode="edit"
          close={() => closeModal(setShowEditUser)}
          sources={sources}
          companies={companies}
          statuses={statuses}
        />
      )}
      {showAddMail ? (
        <AddMail
          companies={companies}
          user={props.user}
          close={() => closeModal(setShowAddMail)}
        />
      ) : (
        ""
      )}
      {showEditMail && editMail ? (
        <AddMail
          companies={companies}
          user={props.user}
          editMail={editMail}
          mode={"edit"}
          close={() => closeModal(setShowEditMail)}
        />
      ) : (
        ""
      )}
      {showAddMailTemplate ? (
        <AddMailTemplate
          companies={companies}
          user={props.user}
          close={() => closeModal(setShowAddMailTemplate)}
        />
      ) : (
        ""
      )}
      {showEditMailTemplate && editMailTemplate ? (
        <AddMailTemplate
          companies={companies}
          user={props.user}
          editMail={editMailTemplate}
          mode={"edit"}
          close={() => closeModal(setShowEditMailTemplate)}
        />
      ) : (
        ""
      )}
      {showAddStatus && (
        <AddStatus
          user={props.user}
          mode="add"
          close={() => closeModal(setShowAddStatus)}
          sources={sources}
          companies={companies}
        />
      )}
      {showEditStatus && editStatus && (
        <AddStatus
          user={props.user}
          statusEdit={editStatus}
          mode="edit"
          close={() => closeModal(setShowEditStatus)}
          sources={sources}
          companies={companies}
        />
      )}

      {/* ── Header ── */}
      <div className="rp-header">
        <div className="rp-header-icon">
          <Settings size={22} />
        </div>
        <div>
          <h2 className="rp-title">Ustawienia</h2>
          <p className="rp-subtitle">
            Zarządzaj źródłami, działami, użytkownikami i statusami
          </p>
        </div>
      </div>

      <div className="adm-tabs-bar">
        <div className="adm-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                className={`tc-tab ${tab === t.id ? "tc-tab--active" : ""}`}
                onClick={() => setTab(t.id)}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
        {currentAdd && (
          <button
            className="lq-btn lq-btn--primary adm-add-btn"
            onClick={currentAdd.onClick}
          >
            <Plus size={14} /> {currentAdd.label}
          </button>
        )}
      </div>

      <div className="adm-content">
        {tab === 0 && (
          <>
            <p className="adm-desc">
              Źródła definiują dostawców leadów. Tylko źródła zdefiniowane w
              systemie są dostępne do importu oraz ręcznego dodawania kontaktu.
            </p>
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nazwa</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => (
                    <tr key={s.id}>
                      <td className="lq-td-id">{s.id}</td>
                      <td>{s.name}</td>
                      <td>
                        {s.name !== "Polecenie" && s.name !== "CC" && (
                          <button
                            className="tc-action-btn tc-action-btn--accept"
                            style={{ color: "#ef4444", borderColor: "#ef4444" }}
                            onClick={() => deleteSource(s.id)}
                          >
                            <Trash2 size={13} /> Usuń
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {sources.length === 0 && (
                    <tr>
                      <td colSpan={3} className="lq-empty-row">
                        Brak źródeł
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 1 && (
          <>
            <p className="adm-desc">
              Administratorzy mają dostęp do wszystkich funkcji. Użytkownicy
              widzą tylko przypisane sobie leady oraz zadania.
            </p>
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th>E-mail</th>
                    <th>Telefon</th>
                    <th>Działy</th>

                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td className="lq-td-mono">{u.mail}</td>
                      <td className="lq-td-mono">{u.phone}</td>
                      <td>{u.departs}</td>

                      <td>
                        {u.name !== "Rozdzielacz" ? (
                          <button
                            className="tc-action-btn tc-action-btn--view"
                            onClick={() => {
                              setEditUser(u);
                              setShowEditUser(true);
                            }}
                          >
                            <Edit2 size={13} /> Edytuj
                          </button>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={6} className="lq-empty-row">
                        Brak użytkowników
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 2 && (
          <>
            <p className="adm-desc">
              Działy definiują podział leadów. Tylko działy zdefiniowane w
              systemie są dostępne do importu oraz ręcznego dodawania kontaktu.
            </p>
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nazwa</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.id}>
                      <td className="lq-td-id">{c.id}</td>
                      <td>{c.name}</td>
                      <td>
                        <button
                          className="tc-action-btn tc-action-btn--accept"
                          style={{ color: "#ef4444", borderColor: "#ef4444" }}
                          onClick={() => deleteCompany(c.id)}
                        >
                          <Trash2 size={13} /> Usuń
                        </button>
                      </td>
                    </tr>
                  ))}
                  {companies.length === 0 && (
                    <tr>
                      <td colSpan={3} className="lq-empty-row">
                        Brak działów
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 3 && (
          <>
            <p className="adm-desc">
              Statusy określają poziom zaawansowania obróbki leada. Wybrany
              kolor reprezentuje status na liście leadów.
            </p>
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th>Kolor</th>
                    <th>Pozycja</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {statuses.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>
                        <span
                          className="adm-color-dot"
                          style={{ background: s.color }}
                        ></span>
                        <span className="adm-color-hex">{s.color}</span>
                      </td>
                      <td>{s.poz}</td>
                      <td>
                        <button
                          className="tc-action-btn tc-action-btn--view"
                          onClick={() => {
                            setEditStatus(s);
                            setShowEditStatus(true);
                          }}
                        >
                          <Edit2 size={13} /> Edytuj
                        </button>
                      </td>
                    </tr>
                  ))}
                  {statuses.length === 0 && (
                    <tr>
                      <td colSpan={4} className="lq-empty-row">
                        Brak statusów
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 4 && (
          <>
            <p className="adm-desc">Maile wysyłane do klientów</p>
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th>Temat</th>
                    <th>Dział</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {mails.map((m) => (
                    <tr key={m.id}>
                      <td>{m.name}</td>
                      <td>{m.subject}</td>
                      <td>{m.company}</td>
                      <td>
                        <button
                          className="tc-action-btn tc-action-btn--view"
                          onClick={() => {
                            setEditMail(m);
                            setShowEditMail(true);
                          }}
                        >
                          <Edit2 size={13} /> Edytuj
                        </button>
                      </td>
                    </tr>
                  ))}
                  {statuses.length === 0 && (
                    <tr>
                      <td colSpan={4} className="lq-empty-row">
                        Brak maili
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 5 && (
          <>
            <p className="adm-desc">
              Konfiguracja wysyłki maili wysyłanych do klientów
            </p>
            <div className="lq-table-wrap db-table-scroll">
              <table className="lq-table">
                <thead>
                  <tr>
                    <th>Nazwa</th>
                    <th>ID szablonu</th>
                    <th>ID serwisu</th>
                    <th>Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {mailTemplates.map((m) => (
                    <tr key={m.id}>
                      <td>{m.company}</td>
                      <td>{m.templateId}</td>
                      <td>{m.serviceId}</td>
                      <td>
                        <button
                          className="tc-action-btn tc-action-btn--view"
                          onClick={() => {
                            setEditMailTemplate(m);
                            setShowEditMailTemplate(true);
                          }}
                        >
                          <Edit2 size={13} /> Edytuj
                        </button>
                      </td>
                    </tr>
                  ))}
                  {statuses.length === 0 && (
                    <tr>
                      <td colSpan={4} className="lq-empty-row">
                        Brak konfiguracji
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
